import { createFileRoute, Link } from '@tanstack/react-router'
import { AccessDenied } from '@/components/permission-guard'
import { useEvent } from '@/lib/api/queries'
import { usePermission } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { SchedulesTab } from '@/features/promotions/schedules-tab'
import { ReportTab } from '@/features/promotions/report-tab'
import { FallbackTab } from '@/features/promotions/fallback-tab'

const TABS = ['schedules', 'report', 'fallback'] as const
type Tab = (typeof TABS)[number]
const TAB_LABEL: Record<Tab, string> = {
  schedules: 'Schedules', report: 'Report', fallback: 'Needs review',
}
// The report and the fallback orders are gated on reporting server-side
// (vendor/discount_api.py `_reporting_vendor_ids`), not on can_view_discounts:
// a vendor or location admin with reporting alone gets them for their vendor.
const REPORTING_TABS: readonly Tab[] = ['report', 'fallback']

export const Route = createFileRoute('/_authenticated/events/$eventId/promotions')({
  component: PromotionsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } => {
    const tab = typeof search.tab === 'string' && (TABS as readonly string[]).includes(search.tab) ? (search.tab as Tab) : undefined
    return tab ? { tab } : {}
  },
})

function PromotionsPage() {
  const { eventId } = Route.useParams()
  const { tab: requestedTab } = Route.useSearch()
  const eventIdNum = Number(eventId)
  const canViewDiscounts = usePermission('can_view_discounts')
  const canViewReporting = usePermission('can_view_reporting')
  const canEditEvents = usePermission('can_edit_events')
  const { data: event } = useEvent(eventIdNum)

  // Schedules need can_view_discounts; the report tabs need reporting alone.
  const tabs = TABS.filter((t) => (REPORTING_TABS.includes(t) ? canViewReporting : canViewDiscounts))
  // Permission guard - now safe after all hooks
  if (!tabs.length) {
    return <AccessDenied message="You don't have permission to view promotions for this event." />
  }
  const tab: Tab = requestedTab && tabs.includes(requestedTab) ? requestedTab : tabs[0]

  return (
    <div className="max-w-[100vw] md:max-w-[1300px] w-full mx-auto px-4 md:px-8 py-6 space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">Promotions</h1>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
          {event?.name ? `${event.name} · ` : ''}Scheduled discounts on menu items: what is discounted, when, and what it has taken off.
        </p>
      </div>

      {event && !event.enable_discount_schedules && (
        <div
          role="status"
          className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          Promotions are switched off for this event. Schedules can be prepared now;{' '}
          {canEditEvents ? (
            <>
              turn them on in{' '}
              <Link to="/events/$eventId/settings" params={{ eventId }} className="font-medium underline underline-offset-2">
                Settings
              </Link>{' '}
              when you're ready.
            </>
          ) : (
            'an event admin can turn them on in Settings.'
          )}
        </div>
      )}

      <nav className="flex gap-1 border-b border-neutral-200 dark:border-neutral-800 overflow-x-auto">
        {tabs.map((t) => (
          <Link
            key={t}
            to="/events/$eventId/promotions"
            params={{ eventId }}
            search={t === 'schedules' ? {} : { tab: t }}
            className={cn('px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px',
              tab === t
                ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white')}
          >
            {TAB_LABEL[t]}
          </Link>
        ))}
      </nav>

      {tab === 'schedules' && <SchedulesTab eventId={eventIdNum} />}
      {tab === 'report' && <ReportTab eventId={eventIdNum} />}
      {tab === 'fallback' && <FallbackTab eventId={eventIdNum} />}
    </div>
  )
}
